/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Download,
  Eye,
  //Trash2,
  ArrowUpRight,
  ArrowDownLeft,
  FileText,
  Clock,
} from "lucide-react";
import React from "react";

import { useInfiniteFaxHistory } from "@/app/(authenticated)/fax/_modules/hooks/getFaxHistory";
import type { Fax } from "@/app/(authenticated)/fax/_modules/types/types";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import type { FaxStatusFilter } from "@/app/_module/types/fax";
import { getApiUrl } from "@/app/_module/services/config";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";

// Format phone number from +1xxxxxxxxxx or xxxxxxxxxx to (xxx) xxx-xxxx
const formatPhoneNumber = (phoneNumber: string | null | undefined): string => {
  if (!phoneNumber) return 'Unknown';

  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Check if it's a valid US number (10 or 11 digits)
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    const digits = cleaned.substring(1);
    return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
  } else if (cleaned.length === 10) {
    return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`;
  }

  // Return original if not a standard format
  return phoneNumber;
};

export const FaxHistory: React.FC<{ search?: string; status?: FaxStatusFilter; direction?: "all" | "inbound" | "outbound" }> = function({ search, status, direction }) {
  const { user } = useUserContext();
  const isEnterprise = user?.user?.accountType === 'enterprise';

  const mapStatus = (s: FaxStatusFilter | undefined) => (s === "received" ? "all" : (s ?? "all"));

  const {
    faxes,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteFaxHistory({ search, status: mapStatus(status), direction, limit: 20 });

  // Faxes come in mixed (inbound uses receivedAt, outbound uses sentAt, some
  // only have createdAt) and across paginated fetches, so the raw list is out of
  // order. Sort by the effective timestamp, newest first, for a clean feed.
  const faxTime = (f: Fax) =>
    new Date((f.sentAt ?? f.receivedAt ?? f.createdAt) as string | number | Date).getTime() || 0;
  const sortedFaxes = React.useMemo(
    () => [...faxes].sort((a, b) => faxTime(b) - faxTime(a)),
    [faxes],
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <p className="text-muted-foreground">Loading fax history...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <p className="text-red-500">
            Failed to load fax history: {String(error)}
          </p>
        </CardContent>
      </Card>
    );
  }
  const getStatusColor = (s: Fax["status"]) => {
    switch (s) {
      case "delivered":
        return "bg-green-100 text-green-800";
      case "sent":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "received":
        return isEnterprise ? "bg-purple-100 text-purple-800" : "bg-gray-100 text-gray-800";
      case "cancelled":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getDirectionIcon = (d: Fax["direction"]) =>
    d === "outbound" ? (
      <ArrowUpRight className="h-3 w-3" />
    ) : (
      <ArrowDownLeft className="h-3 w-3" />
    );

  const getDirectionColor = (d: Fax["direction"]) =>
    d === "outbound" ? "text-green-600" : "text-blue-600";
  
  const handleViewFax = async (fax: Fax) => {
    const firstDoc = fax.documents?.[0];
    if (!firstDoc) return;

    try {
      // Fetch document through backend proxy with auth
      const { getSupabaseClient } = await import('@/app/_module/services/supabaseClient');
      const { endpoint } = await import('@/app/_module/services/endpoints');

      const supabase = await getSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const response = await fetch(getApiUrl(endpoint.fax.getDocument(fax.id, 0)), {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch document');
      }

      // Create blob URL and open in new tab
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");

      // Clean up blob URL after a delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      console.error('Failed to view fax:', error);
    }
  };

  const handleDownloadFax = async (fax: Fax) => {
    const firstDoc = fax.documents?.[0];
    if (!firstDoc) return;

    try {
      // Fetch document through backend proxy with auth
      const { getSupabaseClient } = await import('@/app/_module/services/supabaseClient');
      const { endpoint } = await import('@/app/_module/services/endpoints');

      const supabase = await getSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const response = await fetch(getApiUrl(endpoint.fax.getDocument(fax.id, 0)) + '?download=true', {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch document');
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = firstDoc.originalname || firstDoc.path.split("/").pop() || "fax.pdf";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) {
          fileName = match[1];
        }
      }

      // Create blob URL and trigger download
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up blob URL
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Failed to download fax:', error);
    }
  };

  // Show empty state when no faxes
  if (faxes.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
          <p className="text-muted-foreground font-medium">No faxes found</p>
          <p className="text-sm text-muted-foreground/70 mt-1">
            {direction === "inbound"
              ? "You haven't received any faxes yet"
              : direction === "outbound"
                ? "You haven't sent any faxes yet"
                : "Your fax history will appear here"}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {sortedFaxes.map((fax) => {
        const firstDoc = fax.documents?.[0];
        const fileName = firstDoc ? (firstDoc.originalname || firstDoc.path.split("/").pop()) : undefined;
        const timestamp = new Date(
          (fax.sentAt ?? fax.receivedAt ?? fax.createdAt) as string | number | Date
        );

        return (
          <Card key={fax.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className={`mt-1 ${getDirectionColor(fax.direction)}`}>
                    {getDirectionIcon(fax.direction)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-foreground">
                        To: {formatPhoneNumber(fax.to)}
                      </p>
                      <Badge
                        className={getStatusColor(fax.status)}
                        variant="secondary"
                      >
                        {fax.status}
                      </Badge>
                    </div>

                    <p className="text-sm text-muted-foreground mb-1">
                      From: {formatPhoneNumber(fax.from)}
                    </p>

                    <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
                      {!!fax.metadata?.numOfPages && (
                        <div className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {fax.metadata.numOfPages} page
                          {fax.metadata.numOfPages !== 1 ? "s" : ""}
                        </div>
                      )}
                      {!!fileName && (
                        <div className="flex items-center gap-1">
                          <span>{fileName}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {timestamp.toLocaleString()}
                      </div>
                    </div>

                    {!!fax.failureReason && (
                      <p className="text-sm text-muted-foreground bg-gray-50 p-2 rounded">
                        {fax.failureReason}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1 ml-2">
                  {fax.status !== "pending" && (
                    <React.Fragment>
                      <Button
                        className="h-8 w-8 p-0"
                        onClick={() => handleViewFax(fax)}
                        size="sm"
                        variant="ghost"
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                      <Button
                        className="h-8 w-8 p-0"
                        onClick={() => handleDownloadFax(fax)}
                        size="sm"
                        variant="ghost"
                      >
                        <Download className="h-3 w-3" />
                      </Button>
                    </React.Fragment>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      <div className="flex justify-center pt-2">
        {!!hasNextPage && (
          <Button disabled={isFetchingNextPage} onClick={() => fetchNextPage()} size="sm" variant="outline">
            {isFetchingNextPage ? "Loading..." : "Load more"}
          </Button>
        )}
      </div>
    </div>
  );
};
